<template>
  <div class="flex flex-col bg-white shrink-0 w-72 md:ml-0 lg:w-80">
    <div class="px-6 py-4 border-b border-gray-300 border-solid lg:hidden">
      <slot name="sort" />
    </div>
    <SideNavCategories
      v-if="isPLP && breadcrumb.children"
      :breadcrumb="breadcrumb"
      :is-disabled="isDisabled"
      class="block px-6 py-4 border-b border-gray-300 border-solid"
    />
    <div v-if="!isPLP" class="py-4 border-b border-gray-300 border-solid">
      <CHeading :level="3" :display="5" class="pl-6 mb-4 text-primary-900">{{ t('categories') }}</CHeading>
      <SideNavAttributeFilter
        v-for="(facet, index) in categoryFacets"
        :key="index + facet.label"
        class="pr-0.5"
        :display-name="facet.label"
        :facet="facet"
      />
    </div>
    <CollapsableSection
      v-if="groupedFacets.singleValueFacets.length || showStockedFilter"
      :title="t('delivery_methods_header')"
      :is-open="isDeliveryOptionsOpen"
      collapsable-content-classes="px-2 pb-4 border-b border-solid border-gray-200"
      @update:open="(state) => setIsDeliveryOptionsOpen(state)"
    >
      <client-only>
        <SelectDelivery v-if="isLoggedIn" />
      </client-only>
      <div
        v-for="(facet, index) in groupedFacets.singleValueFacets"
        :key="`single-value-facet-${index}`"
        class="px-4 pt-4"
      >
        <SideNavSingleValueFilter
          v-if="facet.name === 'supplyGroup' && showStockedFilter"
          :checked="isFilterChecked(facet)"
          :facet-name="facet.name"
          :filter="stockFilter"
          :checkbox-text="t('show_only_stocked_products')"
        />
        <div v-else-if="facet.name === 'Butiksförpackat'" class="flex items-center justify-between">
          <SideNavSingleValueFilter
            :checked="isFilterChecked(facet)"
            :facet-name="facet.name"
            :filter="storePackagedFilter"
            :checkbox-text="t('show_only_store_packaged_products')"
          />
          <div class="cursor-pointer" @click="setIsStorePackagedInfoModalVisible(true)">
            <CIcon name="info" />
          </div>
        </div>
      </div>

      <SideNavSingleValueFilter
        v-if="brandConfig.additions.useStockedAtForStockedFilter && showStockedFilter"
        class="px-4 pt-4"
        :facet-name="STOCKED_AT_NAME"
        :checked="!!route.query[STOCKED_AT_NAME]"
        :filter="stockedAtFilter"
        :checkbox-text="t('show_only_stocked_products')"
      />
    </CollapsableSection>
    <div v-for="(facet, index) in groupedFacets.attributeFacets" :key="`attribute-facet-${index}`">
      <SideNavAttributeFilter
        v-if="facet.name && facet.items.length"
        :display-name="attributeDisplayName(facet)"
        :facet="facet"
      />
    </div>

    <LazyStorePackagedInfoModal
      v-if="overlayComponents.storePackagedInfoModal"
      @close="setIsStorePackagedInfoModalVisible(false)"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useProductStore, useUserStore, useBrandStore, useUiStore } from '@/stores';
import { Facet } from '~/types/products';

type Props = {
  breadcrumb?: {
    children?: [];
  };
  isDisabled?: boolean;
};

withDefaults(defineProps<Props>(), {
  breadcrumb: () => ({}),
  isDisabled: false,
});

const BRAND = 'brand';
const CLASSIFICATION_NAME = 'classificationName';
const ETIM_CLASS = 'etimClass';
const dahlIconNameMap = {
  Fästteknik: 'fastenings',
  Sprinkler: 'sprinkler',
  'VVS & Industri': 'vvs-industri',
  'Verktyg & Förnödenheter': 'verktyg',
  'Mark & VA': 'mark-va',
  Fastighet: 'fastighet',
  Kyla: 'kyla',
  El: 'el',
  TestDahlKatalog: 'el',
};

const { appliedFilters, resultFacets, sortSequence, disabledFacets } = storeToRefs(useProductStore());
const { isLoggedIn } = storeToRefs(useUserStore());
const { brandConfig } = useBrandStore();
const route = useRoute();
const { t } = useI18n();
const uiStore = useUiStore();
const { setOverlayComponents } = uiStore;
const { overlayComponents } = storeToRefs(uiStore);
const productStore = useProductStore();

const isDeliveryOptionsOpen = ref(true);
const isPLP = computed(() => route.name === 'kategorier-slug');
const filteredFacets = computed(() => {
  const IGNORED_FACETS = ['filterTags', STOCKED_AT_NAME, 'stockedAt', 'categoryLeafs'];
  return resultFacets.value.filter((facet) => !IGNORED_FACETS.includes(facet.name));
});

const showStockedFilter = computed(() => {
  if (brandConfig.additions.onlyShowStockedFilterWhenLoggedIn) {
    return isLoggedIn.value;
  }
  return true;
});

const groupedFacets = computed(() => {
  const SINGLE_VALUE_FACETS = ['supplyGroup', 'Butiksförpackat'];
  const filtered = filteredFacets.value.reduce(
    (result, facet) => {
      if (SINGLE_VALUE_FACETS.includes(facet.name)) {
        result.singleValueFacets.push(facet);
      } else {
        result.attributeFacets.push(facet);
      }

      return result;
    },
    { singleValueFacets: [], attributeFacets: [] },
  );

  if (disabledFacets.value.length > 0) {
    // remove disabled facets
    filtered.attributeFacets = filtered.attributeFacets.filter(
      (item: Facet) => !disabledFacets.value.includes(item.name as string),
    );
  }

  if (sortSequence.value?.length > 0) {
    // sort by set order
    filtered.attributeFacets = sortAttributesWithSequencing(filtered.attributeFacets, sortSequence?.value, 'name');
  }

  return filtered;
});

const categoryFacets = computed(() => {
  const categoryLeafs = resultFacets.value.find((facet) => facet.name === 'categoryLeafs');
  if (!categoryLeafs?.items?.length) return [];
  const categoryMap = categoryLeafs.items.reduce((acc: any, curr: any) => {
    const [root, leaf] = curr.item.split('|');
    const rootCategory = acc[root] ?? {
      count: 0,
      iconName: (dahlIconNameMap as { [key: string]: string })[root as string],
      items: [],
      label: root,
    };
    rootCategory.items.push({
      count: curr.count,
      displayValue: leaf,
      item: curr.item,
      selected: curr.selected,
    });
    rootCategory.count += curr.count;
    return { ...acc, [root]: rootCategory };
  }, {});
  return Object.values(categoryMap).map((item: any) => ({
    name: 'categoryLeafs',
    ...item,
  }));
});

const stockFilter = computed(() => ({ supplyGroup: ['LAG', t('stock_item'), 'VIR', t('virtual_stock')] }));
const stockedAtFilter = computed(() => ({ [STOCKED_AT_NAME]: 'Ja' }));
const storePackagedFilter = computed(() => ({ Butiksförpackat: 'Ja' }));

const attributeDisplayName = (facet: any) => {
  switch (facet.name) {
    case BRAND:
      return t('brand');
    case CLASSIFICATION_NAME:
      return t('classification_quality_labeling');
    case ETIM_CLASS:
      return t('etim_class');
    default:
      return facet.label;
  }
};

const setIsStorePackagedInfoModalVisible = (val: boolean) => {
  setOverlayComponents({ components: { storePackagedInfoModal: val } });
};
const setIsDeliveryOptionsOpen = (val: boolean) => {
  isDeliveryOptionsOpen.value = val;
};
const isFilterChecked = (filter: any) => {
  return appliedFilters.value.some((f) => f.name === filter.name);
};

onMounted(async () => {
  if (route.params.slug && Array.isArray(route.params.slug)) {
    await productStore.getSequencesByCategory(route.params.slug.join('-'));
  }
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'SideNav',
});
</script>
